import _ from "underscore";
import {
  AccountShape,
  BorrowerEntity,
  LoanApplicationBorrowers,
  OwnedRealEstate,
  ReoOwnershipStatuses,
} from "@eave/shared/types/api";
import BorrowerEntityUtil from "@eave/shared/utils/borrowers";
import { ArrayTwoOrMore } from "@eave/shared/types/generic";
import { SumSectionHeadingProps } from "../../../shared/SectionHeading/SumSectionHeading";

const AssetSummary = {
  getPendingSaleProperties(
    borrowerEntity: BorrowerEntity,
    ownedRealEstate: OwnedRealEstate[],
  ): OwnedRealEstate[] {
    return ownedRealEstate.filter((re: OwnedRealEstate) => {
      const entity = new BorrowerEntityUtil(re.borrower_entity);
      if (!entity.has(borrowerEntity) || !this.isLowestEntity(entity, borrowerEntity)) {
        return false;
      }
      return re.operator_ownership_status
        ? re.operator_ownership_status === ReoOwnershipStatuses.IsBeingSold
        : re.ownership_status === ReoOwnershipStatuses.IsBeingSold;
    });
  },

  /**
   * getSortedAccounts filters by borrowerEntity, account.ignored, and the showAll boolean, and sorts the
   * remaining accounts by order of institution, number_suffix, and ascending account.value
   *
   * Please see function logic for the details if you are unsure.
   * @param borrowerEntity
   * @param accounts
   * @param showAll
   */
  getSortedAccounts(
    borrowerEntity: BorrowerEntity,
    accounts: AccountShape[],
    showAll: boolean,
  ): AccountShape[] {
    return _.chain(accounts)
      .reject(acct => !this.isAccountForBorrower(acct, borrowerEntity))
      .reject((acct: any) => !showAll && acct.ignored && !acct.type)
      .sortBy((acct: any) => -Math.abs(acct.value))
      .sortBy((acct: any) => acct.number_suffix)
      .sortBy((acct: any) => acct.institution)
      .sortBy((acct: any) => acct.ignored) // highest priority
      .value();
  },

  /**
   * isAccountForBorrower returns a boolean based on the borrower_entity argument and the account's
   * borrower_entity to determine if the account belongs to the borrower.
   * @param acct
   * @param borrowerEntity
   */
  isAccountForBorrower(acct: AccountShape, borrowerEntity: BorrowerEntity): boolean {
    // preserve functionality to assign no borrower entity accounts to borrower 1
    if (
      (!acct.borrower_entity || acct.borrower_entity === BorrowerEntity.BorrowerNone) &&
      borrowerEntity === BorrowerEntity.Borrower1
    ) {
      return true;
    }
    const entity = new BorrowerEntityUtil(acct.borrower_entity);
    // reject the accounts wherein the borrowerEntity argument isn't included in the decomposed array
    // or where it isn't the lowest one. Accounts, etc., always "belong" to the lowest entity borrower
    return (
      entity.decomposed(entity).includes(borrowerEntity) &&
      this.isLowestEntity(entity, borrowerEntity)
    );
  },

  /**
   * getAddends takes in assets, which is an array asset numbers that are specifically ordered by borrower,
   * and the loan application borrowers on an application and returns the required object to render the
   * SumSection for assets summary hurdle....
   * @param assets
   * @param borrowers
   */
  getAddends(
    assets: number[],
    borrowers: LoanApplicationBorrowers,
  ): ArrayTwoOrMore<SumSectionHeadingProps> {
    const addends: ArrayTwoOrMore<SumSectionHeadingProps> = [
      {
        value: assets[0],
        description: "Borrower 1 Total Assets",
      },
      borrowers[BorrowerEntity.Borrower2]
        ? {
            value: assets[1],
            description: "Borrower 2 Total Assets",
          }
        : {
            value: 0,
            description: "No Coborrower",
          },
    ];
    if (borrowers[BorrowerEntity.Borrower3]) {
      addends.push({
        value: assets[2] ? assets[2] : 0,
        description: "Borrower 3 Total Assets",
      });
    }
    if (borrowers[BorrowerEntity.Borrower4]) {
      addends.push({
        value: assets[3] ? assets[3] : 0,
        description: "Borrower 4 Total Assets",
      });
    }
    return addends;
  },

  /**
   * isLowestEntity returns whether the `be: BorrowerEntity` argument is the lowest entity in the decomposed
   * BorrowerEntityUtil set
   * @param e
   * @param be
   */
  isLowestEntity(e: BorrowerEntityUtil, be: BorrowerEntity): boolean {
    const minEnt = _.min(e.decomposed(e));
    return minEnt === be;
  },
};

export default AssetSummary;
